import {
    IconButton,
    mergeStyles,
    TooltipHost,
} from "@fluentui/react";
import {
    getNextColor,
    IChartProps
} from "@fluentui/react-charting";
import { useId } from "@fluentui/react-hooks";
import {
    Triangle12Filled,
    TriangleDown12Filled,
    AppsAddInFilled
} from "@fluentui/react-icons";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChartComponent } from "../../../ChartComponents/LineChartFluent";
import { CarouselProps } from "../../../interfaces/CarouselProps.interfaces";
import { IChartVariances } from "../../../interfaces/chart-props.interfaces";
import { IProductKPI, MetricAverages } from "../../../interfaces/customer-product-v2.interfaces";
import CustomCard from "../../../SharedComponents/CustomCard";
import FlagIndicatorsRow from "../../../SharedComponents/FlagIndicatorsRow";
import { CUSTOM_CARD } from "../../../SharedComponents/Styles/CustomCard.styles";
import { iconButtonStyles } from "../../../SharedComponents/Styles/CustomCardWithMetrics.styles";
import * as styles from "./ProductTelemetryChart.styles";
import { FluentProvider, webLightTheme, Button } from "@fluentui/react-components";

const defaultStyles = CUSTOM_CARD.CUSTOM_CARD_DEFAULT_STYLES;

export const ProductTelemetryDailyChart: React.FC<CarouselProps> = ({
  carouseldata,
  granularity,
  application,
  selectedFamily,
  tenantId,
  disableVariances = false,
  disableFlagIndicator = false
}) => {
  const navigate = useNavigate();
  const tooltipId = useId("tooltip");
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState<number | undefined>();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  useEffect(() => {
    setCurrentDate(new Date()); // Reset to today on filter change
  }, [application, granularity]);

  useLayoutEffect(() => {
    const calculateResponsiveWidth = () => {
      if (containerRef.current) {
        const availableWidth = containerRef.current.offsetWidth;
        const newWidth = Math.floor(availableWidth / 1) - 16;
        setCardWidth(newWidth);
      }
    };
    calculateResponsiveWidth();
    window.addEventListener("resize", calculateResponsiveWidth);
    return () => window.removeEventListener("resize", calculateResponsiveWidth);
  }, []);

  const CardVariance = ({ variances }: { variances: IChartVariances[] }) => {
    const rootStyle = variances.length < 2 ? styles.rootVarianceStyleSingle : styles.rootVarianceStyle;
    return (
      <div style={{ ...rootStyle, width: cardWidth ?? 0 }}>
        {variances.map((v, index) => (
          <div key={`variance-${index}`} className={mergeStyles(styles.varianceContainerStyle)}>
            <div
              style={{
                alignItems: "center",
                borderBottom: "1px solid #d5d5d580",
                color: getNextColor(index),
                display: "flex",
                fontWeight: "bold",
                marginBottom: 2
              }}
            >
              <div className={mergeStyles(styles.varianceMetricNameStyle)} title={v.name}>
                {v.name}
              </div>
              {selectedFamily === "Microsoft Sentinel" ? (
                <span style={{ marginLeft: "4px", paddingTop: "2px", fontSize: "10px", color: "#666" }}>
                  {v.description || "(Total data ingested - GB/day)"}
                </span>
              ) : (
                <TooltipHost content={v.description || "Total data ingested - GB/day"} id={tooltipId}>
                  <IconButton iconProps={{ iconName: "Info" }} aria-label="Info" />
                </TooltipHost>
              )}
            </div>
            {selectedFamily === "Microsoft Sentinel" ? (
              <>
                <div>Rolling Monthly Average Ingestion</div>
                <div style={styles.currMonthValStyle}>
                  {v.averageVariance !== undefined && Number.isFinite(v.averageVariance)
                    ? (v.averageVariance / 1000).toFixed(2) + "K"
                    : "0"}
                </div>
              </>
            ) : (
              <>
                <div>This month (till date)</div>
                <div style={styles.currMonthValStyle}>{v.variance.nextValue || "0"}</div>
              </>
            )}
            {selectedFamily !== "Microsoft Sentinel" ? (
              <>
                <div style={v.variance.diffValue.includes("-") ? styles.varianceRedStyle : styles.varianceGreenStyle}>
                  {v.variance.diffValue.includes("-") ? (
                    <TriangleDown12Filled />
                  ) : v.variance.diffValue !== "0" ? (
                    <Triangle12Filled />
                  ) : null}
                  <div>
                    {v.variance.diffValue || 0} ({v.variance.diffPerc || "0%"})
                  </div>
                </div>
                <div>v/s previous month ({v.variance.prevValue || 0})</div>
              </>
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  const renderChartItem = (metricData: IProductKPI, chartIndex: number) => {
    const rawMetricLines = metricData.data ?? [];

    const filteredLines = rawMetricLines.map((metric, index) => {
      const matchingPoints = metric.data
                .filter(point => {
          const matchAgg = granularity ? point.aggregationType === granularity : true;
                    const matchApp = ["Microsoft Purview Consumption"].includes(selectedFamily ?? "") && application ? point.application === application : true;
          return matchAgg && matchApp;
        })
                .map(point => ({ x: new Date(point.x), y: Number(point.y) }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

      return {
        legend: metric.name,
        data: matchingPoints,
        color: getNextColor(index),
        lineOptions: { lineBorderWidth: "4" }
      };
    });

        const allDataPoints = filteredLines.flatMap(line => line.data).sort((a, b) => a.x.getTime() - b.x.getTime());

    if (allDataPoints.length === 0) return null;

    const getValidDataWindow = (centerDate: Date) => {
      const attemptDate = new Date(centerDate.getFullYear(), centerDate.getMonth(), centerDate.getDate());
      for (let i = 0; i < 12; i++) {
        const windowEnd = new Date(attemptDate);
        const windowStart = new Date(attemptDate);
        if (selectedFamily === "Microsoft Sentinel") {
          windowStart.setDate(windowEnd.getDate() - 60); // 60-day window
        } else {
          windowStart.setDate(windowEnd.getDate() - 30); // 30-day window
        }

                const linesWithData = filteredLines.map(line => ({
          ...line,
                    data: line.data.filter(point => point.x >= windowStart && point.x <= windowEnd)
        }));

                const hasData = linesWithData.some(line => line.data.length > 0);
        if (hasData) {
          return { visibleLines: linesWithData, windowStart, windowEnd };
        }

        attemptDate.setDate(attemptDate.getDate() - 30);
      }

      return {
                visibleLines: filteredLines.map(line => ({ ...line, data: [] })),
        windowStart: new Date(centerDate),
        windowEnd: new Date(centerDate)
      };
    };

    const { visibleLines, windowStart, windowEnd } = getValidDataWindow(currentDate);

        const hasNext = allDataPoints.some(p => p.x > windowEnd);
        const hasPrevious = allDataPoints.some(p => p.x < windowStart);

    const getAllTickValues = () => {
      const provisioned = visibleLines[0]?.data ? [...visibleLines[0].data] : [];
      const consumed = visibleLines[1]?.data ? [...visibleLines[1].data] : [];
      const allData = Array.from(new Set([...provisioned, ...consumed].map((p) => p.x.getTime()))).map(
        (timestamp) => new Date(timestamp)
      );

      if (selectedFamily === "Microsoft Sentinel") {
        return allData.filter((_, idx) => {
          return idx % 2 === 0;
        });
      } else {
        return allData;
      }
    };

    const tickValues = getAllTickValues();

    const calculateAverageVarianceByMetric = (
      visibleLines: { legend: string; data: { x: Date; y: number }[] }[]
    ): MetricAverages => {
      const result: MetricAverages = {};

      visibleLines.forEach((line) => {
        if (line.data.length === 0) {
          result[line.legend] = 0;
          return;
        }

        const latestTimestamp = Math.max(...line.data.map((point) => point.x.getTime()));
        if (!Number.isFinite(latestTimestamp)) {
          result[line.legend] = 0;
          return;
        }

        const endDate = new Date(latestTimestamp);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 29);

        const filteredPoints = line.data.filter((point) => point.x >= startDate && point.x <= endDate);

        const average =
          filteredPoints.length > 0
            ? filteredPoints.reduce((sum, point) => sum + point.y, 0) / filteredPoints.length
            : 0;

        result[line.legend] = Number(average.toFixed(2));
      });
      return result;
    };

    const chartData: IChartProps = { lineChartData: visibleLines };
    const averageVarianceByMetric = calculateAverageVarianceByMetric(visibleLines);
    const chartVariances: IChartVariances[] = rawMetricLines
            .filter(metric => {
                const hasMonthlyData = metric.data?.some(point => {
                    const isMatchGranularity = ["Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot"].includes(selectedFamily ?? "")
            ? point.aggregationType === "Monthly"
            : true;

                    const matchApp = ["Microsoft Purview Consumption"].includes(selectedFamily ?? "") && application
              ? point.application === application
              : true;

          return isMatchGranularity && matchApp;
        });

        return hasMonthlyData;
      })
      .map(({ description, name, variance }) => ({
        description,
        name,
        variance,
        averageVariance: selectedFamily === "Microsoft Sentinel" ? (averageVarianceByMetric[name] ?? 0) : 0
      }));

    return (
      <div key={`chart-${chartIndex}`} style={styles.cardContainerStyle(cardWidth)}>
                <CustomCard cardHeaderTitle={metricData.name} kustoLink={metricData.kustoLink}
          exploreFeaturesComponent={
                        selectedFamily === "Security Copilot"
                            ? (
              <FluentProvider theme={webLightTheme}>
                <Button
                  icon={<AppsAddInFilled style={{ color: "var(--colorBrandBackground)" }} />}
                  appearance="transparent"
                  onClick={() => {
                    navigate(`/customer?id=${tenantId}&tab=features&section=Security+Copilot&granularity=Daily`);
                  }}
                  style={{
                                            transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f5f3f3ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  Explore Features
                </Button>
              </FluentProvider>

                            )
                            : undefined
          }
          flagIndicatorComponent={
            disableFlagIndicator ? undefined : (
              <div className={defaultStyles.cardFooterOuter}>
                <FlagIndicatorsRow
                  isTCT={metricData.isTCT}
                  isCAT={metricData.isCAT}
                  hasEngagedAdmin={metricData.hasEngagedAdmin}
                />
              </div>
            )
          }
        >
          {!disableVariances ? <CardVariance variances={chartVariances} /> : null}
          <LineChartComponent
            data={chartData}
            allowResize={true}
            tickValues={tickValues}
            granularity={granularity}
            selectedFamily={selectedFamily}
            width={cardWidth}
            height={342}
          />
                    <div style={{ display: "flex", justifyContent: "center", position: "relative", zIndex: 999, marginTop: 12, top: -5, gap: 10 }}>
            <IconButton
              iconProps={{ iconName: "ChevronLeft" }}
              title="Previous"
                            onClick={() => setCurrentDate(prev => {
                  const newDate = new Date(prev);
                  newDate.setDate(newDate.getDate() - 30);
                  return newDate;
                            })}
              disabled={!hasPrevious}
              styles={iconButtonStyles}
            />
            <IconButton
              iconProps={{ iconName: "ChevronRight" }}
              title="Next"
                            onClick={() => setCurrentDate(prev => {
                  const newDate = new Date(prev);
                  newDate.setDate(newDate.getDate() + 30);
                  return newDate;
                            })}
              disabled={!hasNext}
              styles={iconButtonStyles}
            />
          </div>
        </CustomCard>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={styles.containerStyle}>
      {carouseldata.items.map((item, index) => renderChartItem(item, index))} {/* ok */}
    </div>
  );
};
